import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import OrderService from "services/OrderService";

const initialState = {
  loading: false,
  orders: [],
  order: {},
  total: 0,
  message: "",
  showMessage: false,
  filters: {
    dateFilter: null,
    startDate: null,
    endDate: null,
    orderStatus: null,
    paymentStatus: null,
  },
};

export const getOrders = createAsyncThunk(
  "orders/getOrders",
  async ({ page = 1, pagelimit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await OrderService.getOrders(page, pagelimit, filters);
      return {
        orders: response.results?.data || response.results || [],
        total: response.count || 0,
        filters,
      };
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching orders");
    }
  }
);
export const getOrder = createAsyncThunk(
  "orders/getOrder",
  async (id, { rejectWithValue }) => {
    try {
      const response = await OrderService.getOrder(id);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching order");
    }
  }
);

export const updateOrders = createAsyncThunk(
  "orders/updateOrders",
  async (data, { rejectWithValue }) => {
    try {
      const response = await OrderService.updateOrders(data);
      return {
        orders: response.results,
        total: response.count, // Assuming the API returns total count
      };
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching orders");
    }
  }
);

export const deleteOrders = createAsyncThunk(
  "orders/deleteOrders",
  async ({ id, currentPage }, { rejectWithValue, dispatch }) => {
    try {
      const response = await OrderService.deleteOrders({ id });
      dispatch(getOrders(currentPage)); // Refresh orders
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error deleting order");
    }
  }
);

// Create the slice for orders
const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle getOrders lifecycle
      .addCase(getOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrders.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.orders = payload.orders;
        state.total = payload.total;
        state.showMessage = false;
      })
      .addCase(getOrders.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(getOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrder.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.order = payload;
        state.showMessage = false;
      })
      .addCase(getOrder.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(updateOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateOrders.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.orders = payload.orders;
        state.total = payload.total;
        state.showMessage = false;
      })
      .addCase(updateOrders.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(deleteOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteOrders.fulfilled, (state) => {
        state.loading = false;
        state.showMessage = false;
      })
      .addCase(deleteOrders.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      });
  },
});

// Export actions and reducer
export const { clearMessage } = orderSlice.actions;
export default orderSlice.reducer;
